interface BareModal {
    open: boolean;
}
type TargetStatusListener = (open: boolean) => any;
type TargetStatusUnsubscribe = () => void;

class ModalManager {
    private modalsByTarget: Map<string, BareModal[]> = new Map();
    private targetByModal: WeakMap<BareModal, string> = new WeakMap();
    private subscriptions: Map<string, TargetStatusListener[]> = new Map();

    private getTarget(modal: BareModal): string | null {
        const target = this.targetByModal.get(modal);
        return target == null ? null : target;
    }

    private getModals(target: string): BareModal[] {
        return this.modalsByTarget.get(target) || [];
    }

    private setModalTarget(modal: BareModal, target: string): void {
        this.targetByModal.set(modal, target);
    }

    private clearModalTarget(modal: BareModal): void {
        this.targetByModal.delete(modal);
    }

    private addModalToTarget(modal: BareModal, target: string): void {
        this.modalsByTarget.set(target, this.getModals(target).concat(modal));
    }

    private removeModalFromTarget(modal: BareModal, target: string): void {
        this.modalsByTarget.set(target, this.getModals(target).filter((x) => x !== modal));
    }

    private getSubscriptions(target: string): TargetStatusListener[] {
        return this.subscriptions.get(target) || [];
    }

    private unsubscribe(target: string, wrappedListener: TargetStatusListener): void {
        this.subscriptions.set(target, this.getSubscriptions(target).filter((x) => x !== wrappedListener));
    }

    public subscribe(target: string, listener: TargetStatusListener): TargetStatusUnsubscribe {
        const wrappedListener: TargetStatusListener = (open) => listener(open);
        this.subscriptions.set(target, this.getSubscriptions(target).concat(wrappedListener));
        return this.unsubscribe.bind(this, target, wrappedListener);
    }

    public getOpenStatus(target: string): boolean {
        return this.getModals(target).some((modal) => modal.open);
    }

    public refresh(target: string): void {
        const open = this.getOpenStatus(target);
        for (const listener of this.getSubscriptions(target)) {
            listener(open);
        }
    }

    public registerModal(modal: BareModal, target: string): void {
        // Ignore when there is no change
        if (this.getTarget(modal) === target) {
            return;
        }

        this.unregisterModal(modal);
        this.setModalTarget(modal, target);
        this.addModalToTarget(modal, target);
        this.refresh(target);
    }

    public unregisterModal(modal: BareModal): void {
        const target = this.getTarget(modal);

        // Ignore when it's already unregistered
        if (target === null) {
            return;
        }

        this.clearModalTarget(modal);
        this.removeModalFromTarget(modal, target);
        this.refresh(target);
    }
}

// PRODUCT-26110 Phase 2B2 Task 5 batch 4: the previous augmentation targeted bare
// `'vue'` (whose real Vue 3 `VueConstructor`/`Vue` types no longer exist at all -- both
// interfaces are gone, so this never merged with anything) and the vue-router-3-era
// dead subpath `'vue/types/vue'` (module-not-found under vue 3's `exports` map, not
// just a stale type). `$modalManager` is only ever read from class components
// (`extends Vue` from the `vue-property-decorator` package name, aliased by this
// package's own tsconfig `paths` to the `@evidentid/vue-property-decorator` fork --
// `Modal.vue`/`ModalTarget.vue`), so the augmentation needs to target the fork's own
// `Vue` interface, reached via that same bare specifier (this package has no real
// `@evidentid/vue-property-decorator` dependency/symlink at all -- only the
// paths-mapped bare name resolves here, confirmed via this package's own
// `node_modules/@evidentid` listing -- so augmenting the scoped name the way
// tprm-portal's `vue-app-injections.d.ts` does would be inert here).
declare module 'vue-property-decorator' {
    interface Vue {
        readonly $modalManager: ModalManager;
    }
}

export default ModalManager;
